// src/oura/pageRoles.ts
//
// Where a reviewer's page-role corrections live.
//
// The Role select in the Pages table used to write to component state and
// nothing else, so every correction was thrown away by the next reload — and a
// correction that does not survive a reload is not worth making. Two roles now
// depend on it entirely (`product` and `placeholder` are how a reviewer says
// "not copy work"), and labelling a forty-page catalogue twice a day is not a
// product.
//
// Two stores, deliberately:
//
//   the backend   the SOURCE OF TRUTH. `PATCH /businesses/{id}/pages` writes a
//                 correction; `GET /businesses/{id}/pages/roles` reads every
//                 correction back in one request. Overrides live in their own
//                 table, so a re-scan cannot undo them.
//   localStorage  a local cache, so the table can paint corrections before the
//                 network answers and keep working if it never does.
//
// Both halves used to be needed because the backend enum held only six roles:
// `product`, `utility` and `placeholder` were suppressed before the request and
// survived in one browser, so one reviewer's set-aside labels were invisible to
// everybody else. ourteam-backend#4368 extended the enum and added the bulk
// read, so the server is now the shared answer and localStorage is only a head
// start on it.

import { getPageRolesResult, setPageRole } from "@service/aeo";
import { PAGE_ROLES, isBackendRole, type PageRole } from "./pageWorkshop";

export const roleStorageKey = (businessId: string) => `oura:page-roles:${businessId}`;

function isPageRole(value: unknown): value is PageRole {
    return typeof value === "string" && (PAGE_ROLES as string[]).includes(value);
}

/**
 * Read back what this browser has labelled, per business.
 *
 * Rebuilt entry by entry rather than cast: a record written by an older build
 * (or hand-edited) must degrade to "no override" for the rows it gets wrong,
 * never take the Pages tab down. An unknown role value is dropped, which falls
 * the row back to inference — the same place it would have been anyway.
 */
export function loadStoredRoles(businessId: string | undefined): Record<string, PageRole> {
    if (!businessId) return {};
    try {
        const raw = localStorage.getItem(roleStorageKey(businessId));
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        const out: Record<string, PageRole> = {};
        for (const [url, role] of Object.entries(parsed as Record<string, unknown>)) {
            if (isPageRole(role)) out[url] = role;
        }
        return out;
    } catch {
        return {};
    }
}

export function storeRoles(
    businessId: string | undefined,
    roles: Record<string, PageRole>,
): void {
    if (!businessId) return;
    try {
        localStorage.setItem(roleStorageKey(businessId), JSON.stringify(roles));
    } catch {
        // Storage full or blocked. The in-memory correction still applies for
        // this session; it just will not survive the next reload.
    }
}

/**
 * Mirror one correction to the backend, if it is one the backend can hold.
 *
 * Fire and forget on purpose: localStorage has already accepted the change and
 * the UI has already moved. A failed PATCH must not roll the label back under
 * the reviewer's cursor — they would have to re-apply it with no way to tell
 * why it bounced. `setPageRole` reports failures to Sentry and resolves false.
 *
 * Roles outside the backend's enum are still not sent at all rather than sent
 * and allowed to 400: a request whose answer we already know is noise in the
 * error log. All nine pass that check today; the guard is for the next role
 * somebody adds to the menu before the enum has it.
 */
export function persistRole(
    businessId: string | undefined,
    url: string,
    role: PageRole,
): void {
    if (!businessId || !isBackendRole(role)) return;
    void setPageRole(businessId, url, role);
}

/**
 * Every correction the backend holds for this business, keyed like the local
 * ones so the two merge.
 *
 * The backend returns PATHS (`/about-us`); this map is keyed by whatever the
 * table asks with, which is an absolute url. `resolveKey` turns a path into the
 * business's own url so the join lands. A role outside the FE's own union is
 * dropped rather than trusted: it would be a value this build does not know how
 * to render, and falling back to inference shows something true.
 */
export async function fetchStoredRoles(
    businessId: string | undefined,
    domain: string | undefined,
): Promise<Record<string, PageRole>> {
    return (await fetchStoredRolesResult(businessId, domain)) ?? {};
}

/**
 * The same corrections, or null when the read failed, so a caller that must
 * not count set-aside pages can tell a failure from "none set aside" (the
 * client's Website page). No business is no corrections, not a failure.
 */
export async function fetchStoredRolesResult(
    businessId: string | undefined,
    domain: string | undefined,
): Promise<Record<string, PageRole> | null> {
    if (!businessId) return {};
    const rows = await getPageRolesResult(businessId);
    if (!rows) return null;
    const out: Record<string, PageRole> = {};
    for (const row of rows) {
        if (!isPageRole(row?.role)) continue;
        const key = resolveKey(row.url, domain);
        if (key) out[key] = row.role;
    }
    return out;
}

/** A stored path resolved against the business's own domain. Returns null for
 *  anything unusable, so a malformed row costs one entry rather than the page. */
function resolveKey(url: string, domain: string | undefined): string | null {
    if (typeof url !== "string" || url.trim() === "") return null;
    const value = url.trim();
    if (/^https?:\/\//i.test(value)) return value;
    if (!domain) return null;
    try {
        return new URL(value, `https://${domain}`).toString();
    } catch {
        return null;
    }
}
